import React from "react";
import { motion } from "framer-motion";
import {
  ShieldCheck,
  Users,
  CalendarCheck,
  Sparkles,
  CheckCircle2
} from "lucide-react";

const FEATURES = [
  {
    number: "01",
    tag: "Core Capability",
    title: "Expertise in BPM and LCNC Solutions",
    icon: ShieldCheck,
    description:
      "With years of experience in Business Process Management (BPM) and cutting-edge Low-Code/No-Code platforms, Truviq delivers solutions that simplify complexity, enhance agility, and unlock unparalleled efficiency for your business.",
    highlights: ["Enterprise BPM Architecture", "Rapid Low-Code Orchestration"],
  },
  {
    number: "02",
    tag: "Client-First Culture",
    title: "Customer-Centric Approach",
    icon: Users,
    description:
      "At Truviq, we put our clients first. Our tailored solutions are designed to address your unique challenges and align seamlessly with your strategic goals, ensuring measurable results and long-term success.",
    highlights: [
      "Custom Strategic Roadmapping",
      "Dedicated Operational Alignment",
    ],
  },
  {
    number: "03",
    tag: "Demonstrated Trust",
    title: "Proven Track Record",
    icon: CalendarCheck,
    description:
      "Our commitment to excellence has earned us the trust of leading organizations across industries. With a history of successful projects, we have consistently delivered impactful outcomes, driving growth and innovation for our clients.",
    highlights: ["100% Milestone Delivery", "Verifiable Impact & ROI"],
  },
  {
    number: "04",
    tag: "Next-Gen Resilience",
    title: "Scalable and Future-Ready Solutions",
    icon: Sparkles,
    description:
      "Our solutions are designed with scalability and sustainability in mind. As your business grows, our systems evolve with you, ensuring you’re always equipped to meet new demands and seize emerging opportunities.",
    highlights: ["Future-Proof Engineering", "Continuous Architecture Audits"],
  },
  {
    number: "05",
    tag: "Get Assured Solutions",
    title: "Dedicated Team of Experts",
    icon: Users,
    description:
      "Our team of seasoned professionals combines technical expertise, business acumen, and a passion for problem-solving to deliver exceptional results. With Truviq, you’re partnering with a team that truly cares about your success.",
    highlights: ["Future-Proof Engineering", "Continuous Architecture Audits"],
  },
];

export default function AboutWhyUs() {
  return (
    <section className="relative w-full bg-[#000036] text-white py-16 md:py-24 lg:py-32  selection:bg-[#62d4f7] selection:text-[#000036]">
      <div className="mx-auto 3xl:w-4/5 lg:w-11/12 px-6 lg:px-12 relative z-10">
        {/* Main 2-Column Split: Note `items-start` which is essential for sticky */}
        <div className="flex flex-col lg:flex-row items-start gap-12 xl:gap-20">
          <div className="lg:w-6/12 lg:sticky lg:top-28 w-full flex flex-col justify-start">
            <motion.div
              initial={{ opacity: 0, y: 15 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
              className="inline-flex items-center gap-2 self-start px-0.5 py-1.5 backdrop-blur-md mb-6"
            >
              <span className="text-[11px] font-bold uppercase tracking-[0.2em] text-white">
                The Truviq Standard
              </span>
            </motion.div>

            <motion.h2
              initial={{ opacity: 0, y: 15 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.1 }}
              className="text-3xl sm:text-4xl md:text-5xl font-bold tracking-tight text-white"
            >
              Why Choose{" "}
              <span className="italic font-serif font-normal text-[#62d4f7]">
                Truviq?
              </span>
            </motion.h2>
            <motion.p
              initial={{ opacity: 0, y: 15 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.2 }}
              className="mt-5 text-base sm:text-lg text-white leading-relaxed mb-8"
            >
              With a team of passionate professionals and cutting-edge
              technology, we ensure unmatched efficiency and precision in every
              project. Choosing Truviq means choosing a partner dedicated to
              driving your success through expertise, collaboration, and a
              customer-first mindset.
            </motion.p>
          </div>
          <div className="lg:w-6/12 w-full flex flex-col gap-6">
            {FEATURES.map((item, idx) => {
              const Icon = item.icon;
              return (
                <div
                  key={item.number}
                  className="group relative rounded-xl bg-white p-6 sm:p-8 text-slate-900 border border-black/10 shadow-[0_15px_40px_rgba(0,0,0,0.12)] transition-all duration-300 hover:shadow-2xl overflow-hidden"
                >
                  <div className="flex items-start justify-between mb-6">
                    <div className="flex flex-col">
                      <span className="font-serif text-5xl sm:text-6xl font-light tracking-tight text-[#000036] transition-all duration-300 group-hover:translate-x-1">
                        {item.number}
                      </span>
                    </div>
                    <div className="h-14 w-14 rounded-2xl bg-slate-50 border border-black/5 shadow-sm flex items-center justify-center text-[#000036] transition-all duration-300 group-hover:bg-[#000036] group-hover:text-white group-hover:-rotate-6">
                      <Icon size={28} strokeWidth={1.75} />
                    </div>
                  </div>
                  <h3 className="text-2xl font-bold tracking-tight text-[#000036] leading-snug mb-4">
                    {item.title}
                  </h3>
                  <p className="text-base leading-relaxed text-slate-600 font-normal md:mb-8 mb-4">
                    {item.description}
                  </p>
                  <div className="pt-5 border-t border-slate-100 flex flex-wrap items-center gap-y-2 gap-x-6">
                    {item.highlights.map((point, pIdx) => (
                      <div
                        key={pIdx}
                        className="flex items-center gap-2 text-sm font-semibold text-slate-500"
                      >
                        <CheckCircle2 size={15} className="text-[#000036]" />
                        <span>{point}</span>
                      </div>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
}
